import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export interface AuditLogEntry {
  action_type: 'view' | 'create' | 'update' | 'delete' | 'download' | 'upload' | 'access'
  resource_type: 'medical_record' | 'document' | 'user_profile' | 'access_control' | 'audit_log'
  resource_id?: string
  resource_description?: string
  accessed_data?: Record<string, any>
  metadata?: Record<string, any>
}

export interface AuditLogOptions {
  anonymize?: boolean
  includeUserAgent?: boolean
  includeIpAddress?: boolean
}

/**
 * Hook para registrar logs de auditoria conforme LGPD
 * Registra automaticamente quem acessou, qual informação foi acessada e quando
 */
export const useAuditLog = () => {
  const { user, userProfile } = useAuth()

  /**
   * Função para anonimizar dados sensíveis conforme LGPD
   */
  const anonymizeData = useCallback((data: Record<string, any>): Record<string, any> => {
    const sensitiveFields = ['cpf', 'rg', 'phone', 'address', 'email', 'date_of_birth']
    const anonymized = { ...data }

    sensitiveFields.forEach(field => {
      if (anonymized[field]) {
        if (typeof anonymized[field] === 'string') {
          // Manter apenas primeiros e últimos caracteres
          const value = anonymized[field] as string
          if (value.length > 4) {
            anonymized[field] = value.substring(0, 2) + '*'.repeat(value.length - 4) + value.substring(value.length - 2)
          } else {
            anonymized[field] = '*'.repeat(value.length)
          }
        } else {
          anonymized[field] = '[DADOS_ANONIMIZADOS]'
        }
      }
    })

    return anonymized
  }, [])

  /**
   * Função para obter informações do navegador e IP
   */
  const getBrowserInfo = useCallback(() => {
    return {
      user_agent: navigator.userAgent,
      session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      // IP será obtido pelo backend se necessário
      ip_address: null
    }
  }, [])

  /**
   * Registra um log de auditoria
   */
  const logAudit = useCallback(async (
    entry: AuditLogEntry,
    options: AuditLogOptions = {}
  ): Promise<void> => {
    try {
      if (!user) {
        console.warn('🔍 [AUDIT] Tentativa de log sem usuário autenticado')
        return
      }

      // Verificar se há sessão válida
      const { data: session, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session.session) {
        console.warn('🔍 [AUDIT] Sessão inválida, pulando log de auditoria')
        return
      }

      const browserInfo = getBrowserInfo()
      
      // Anonimizar dados se solicitado ou se contém dados sensíveis
      const processedData = options.anonymize || entry.accessed_data 
        ? anonymizeData(entry.accessed_data || {})
        : entry.accessed_data

      const auditEntry = {
        user_id: user.id,
        user_email: user.email,
        action_type: entry.action_type,
        resource_type: entry.resource_type,
        resource_id: entry.resource_id,
        resource_description: entry.resource_description,
        accessed_data: processedData,
        user_agent: options.includeUserAgent !== false ? browserInfo.user_agent : null,
        session_id: browserInfo.session_id,
        metadata: {
          user_role: userProfile?.role,
          timestamp_client: new Date().toISOString(),
          ...entry.metadata
        }
      }

      console.log('🔍 [AUDIT] Tentando registrar log:', {
        action: entry.action_type,
        resource: entry.resource_type,
        user: user.email
      })

      // Usar cliente Supabase em vez de fetch manual
      const { error } = await supabase
        .from('audit_logs')
        .insert([auditEntry])

      if (error) {
        console.error('❌ [AUDIT] Erro ao registrar log de auditoria:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        })
        
        // Se for erro de RLS, tentar sem campos opcionais
        if (error.code === '42501') {
          console.log('🔍 [AUDIT] Tentando inserção simplificada devido a RLS')
          const simplifiedEntry = {
            user_id: user.id,
            action_type: entry.action_type,
            resource_type: entry.resource_type,
            resource_id: entry.resource_id
          }
          
          const { error: retryError } = await supabase
            .from('audit_logs')
            .insert([simplifiedEntry])
            
          if (retryError) {
            console.error('❌ [AUDIT] Falha na inserção simplificada:', retryError)
          } else {
            console.log('✅ [AUDIT] Log simplificado registrado com sucesso')
          }
        }
      } else {
        console.log(`✅ [AUDIT] Log registrado: ${entry.action_type} em ${entry.resource_type}`, {
          resource_id: entry.resource_id,
          user: user.email,
          timestamp: new Date().toISOString()
        })
      }
    } catch (error) {
      console.error('❌ [AUDIT] Erro inesperado ao registrar log:', error)
      // Fallback silencioso - não quebrar o fluxo principal
    }
  }, [user, userProfile, anonymizeData, getBrowserInfo])

  /**
   * Funções de conveniência para tipos específicos de auditoria
   */
  const logView = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string, data?: Record<string, any>) => {
    return logAudit({
      action_type: 'view',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description,
      accessed_data: data
    }, { anonymize: true })
  }, [logAudit])

  const logCreate = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string, data?: Record<string, any>) => {
    return logAudit({
      action_type: 'create',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description,
      accessed_data: data
    })
  }, [logAudit])

  const logUpdate = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string, data?: Record<string, any>) => {
    return logAudit({
      action_type: 'update',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description,
      accessed_data: data
    })
  }, [logAudit])

  const logDelete = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string) => {
    return logAudit({
      action_type: 'delete',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description
    })
  }, [logAudit])

  const logDownload = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string, data?: Record<string, any>) => {
    return logAudit({
      action_type: 'download',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description,
      accessed_data: data
    }, { anonymize: true })
  }, [logAudit])

  const logUpload = useCallback((resourceType: AuditLogEntry['resource_type'], resourceId: string, description?: string, data?: Record<string, any>) => {
    return logAudit({
      action_type: 'upload',
      resource_type: resourceType,
      resource_id: resourceId,
      resource_description: description,
      accessed_data: data
    })
  }, [logAudit])

  return {
    logAudit,
    logView,
    logCreate,
    logUpdate,
    logDelete,
    logDownload,
    logUpload,
    anonymizeData
  }
}

export default useAuditLog